import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  BarStack,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import * as z from "zod";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { zodValidator } from "@tanstack/zod-adapter";
import { EyeIcon } from "lucide-react";
import type { ChartConfig } from "@/components/ui/chart";
import type { BreadcrumbItemDef } from "@/components/ui/breadcrumb";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  entityCountByDay,
  entityCountByDepartment,
  entityCountByTypeQueryOptions,
  entityCountByUser,
} from "@/lib/queries/stats.ts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BreadcrumbNav } from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { departmentQueryOptions } from "@/lib/queries/departments.ts";
import { userQueryOptions } from "@/lib/queries/users.ts";
import { entityQueryOptions } from "@/lib/queries/entities.ts";

const chartConfig = {
  ignored_count: {
    label: "Detected",
    color: "#17B26A",
  },
  prevented_count: {
    label: "Prevented",
    color: "#B3B8DB",
  },
} satisfies ChartConfig;

const productSearchSchema = z.object({
  user: z.string().optional(),
  department: z.number().optional(),
  pii: z.coerce.number().optional(),
});

export const Route = createFileRoute("/_app/secret-marker")({
  component: RouteComponent,
  validateSearch: zodValidator(productSearchSchema),
});

function RouteComponent() {
  const { user, department, pii } = Route.useSearch();
  const departmentData = useQuery({
    ...departmentQueryOptions(department!),
    enabled: !!department,
  });

  const userData = useQuery({ ...userQueryOptions(user!), enabled: !!user });

  const piiData = useQuery({ ...entityQueryOptions(pii!), enabled: !!pii });
  // let title = "Activity";
  // if (pii || user || department) title += " for";
  // if (pii && piiData.isSuccess) {
  //   title += ` ${piiData.data.name}`;
  //   if (user || department) title += " by";
  // }
  // if (user && userData.isSuccess) {
  //   title += ` ${userData.data.full_name}`;
  // } else if (department && departmentData.isSuccess) {
  //   title += ` the ${departmentData.data.name} department`;
  // }

  const breadcrumbs: Array<BreadcrumbItemDef> = [
    {
      label: "Secret Marker",
      link: <Link to="/secret-marker">Secret Marker</Link>,
    },
  ];
  if (department && departmentData.isSuccess) {
    if (user) {
      breadcrumbs.push({
        label: departmentData.data.name,
        link: (
          <Link to="." search={(s) => ({ ...s, user: undefined })}>
            {departmentData.data.name}
          </Link>
        ),
      });
    } else {
      breadcrumbs.push({ label: departmentData.data.name });
    }
  }
  if (user && userData.isSuccess) {
    breadcrumbs.push({ label: userData.data.full_name });
  }
  if (pii && piiData.isSuccess) {
    breadcrumbs.push({ label: piiData.data.name });
  }

  const [start_date] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [end_date] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return d;
  });

  return (
    <div className="grid grid-cols-1 gap-3 sm:gap-4">
      {breadcrumbs.length > 1 && <BreadcrumbNav items={breadcrumbs} />}
      <h1 className="text-2xl font-bold sm:text-3xl">Secret Marker</h1>
      <p className="text-muted-foreground">Activity in last 30 days</p>
      <TopCharts
        start_date={start_date}
        end_date={end_date}
        user_id={user}
        department_id={department}
        entity_type_id={pii}
      />
      {!pii && (
        <PiiChart
          start_date={start_date}
          end_date={end_date}
          user_id={user}
          department_id={department}
        />
      )}
      {user ? null : department ? (
        <UsersTable
          start_date={start_date}
          end_date={end_date}
          user_id={user}
          department_id={department}
          entity_type_id={pii}
        />
      ) : (
        <DepartmentsTable start_date={start_date} end_date={end_date} />
      )}
    </div>
  );
}

function UsersTable({
  start_date,
  end_date,
  user_id,
  department_id,
  entity_type_id,
}: {
  start_date: Date;
  end_date: Date;
  user_id?: string;
  department_id?: number;
  entity_type_id?: number;
}) {
  const statsByUser = useQuery(
    entityCountByUser({
      start_date,
      end_date,
      user_id,
      department_id,
      entity_type_id,
    }),
  );

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Scanned</TableHead>
                  <TableHead>Detected</TableHead>
                  <TableHead>Prevented</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statsByUser.isSuccess ? (
                  statsByUser.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-6 text-center">
                        No entities
                      </TableCell>
                    </TableRow>
                  ) : (
                    statsByUser.data.map((stat) => (
                      <TableRow key={stat.user_id}>
                        <TableCell>{stat.full_name}</TableCell>
                        <TableCell>{stat.total_word_count}</TableCell>
                        <TableCell>{stat.ignored_entity_count}</TableCell>
                        <TableCell>{stat.prevented_entity_count}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            className="gap-2 text-[#535862]"
                            asChild
                          >
                            <Link
                              to="."
                              search={(s) => ({
                                ...s,
                                user: stat.user_id,
                              })}
                            >
                              <EyeIcon className="size-4" />
                              Learn more
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="my-2 h-4 w-full" />
                      ))}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DepartmentsTable({
  start_date,
  end_date,
}: {
  start_date: Date;
  end_date: Date;
}) {
  const { pii: entity_type_id } = Route.useSearch();
  const statsByDepartment = useQuery(
    entityCountByDepartment({
      start_date,
      end_date,
      entity_type_id,
    }),
  );

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>Departments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Scanned</TableHead>
                  <TableHead>Detected</TableHead>
                  <TableHead>Prevented</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statsByDepartment.isSuccess ? (
                  statsByDepartment.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-6 text-center">
                        No entities
                      </TableCell>
                    </TableRow>
                  ) : (
                    statsByDepartment.data.map((stat) => (
                      <TableRow key={stat.department_id}>
                        <TableCell>{stat.department_name}</TableCell>
                        <TableCell>{stat.total_word_count}</TableCell>
                        <TableCell>{stat.ignored_entity_count}</TableCell>
                        <TableCell>{stat.prevented_entity_count}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            className="gap-2 text-[#535862]"
                            asChild
                          >
                            <Link
                              to="."
                              search={(s) => ({
                                ...s,
                                department: stat.department_id,
                              })}
                            >
                              <EyeIcon className="size-4" />
                              Learn more
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="my-2 h-4 w-full" />
                      ))}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TopCharts({
  start_date,
  end_date,
  user_id,
  department_id,
  entity_type_id,
}: {
  start_date: Date;
  end_date: Date;
  user_id?: string;
  department_id?: number;
  entity_type_id?: number;
}) {
  const globalStats = useQuery(
    entityCountByDay({
      start_date,
      end_date,
      user_id,
      department_id,
      entity_type_id,
    }),
  );

  const first =
    globalStats.data?.daily_stats.findIndex(
      (stat) => stat.ignored_count + stat.prevented_count > 0,
    ) ?? 0;
  const data = globalStats.data?.daily_stats.slice(
    first,
    globalStats.data.daily_stats.length,
  );
  return (
    <div className="grid gap-4 sm:gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        <Card className="relative overflow-hidden">
          <span className="pointer-events-none absolute top-3 left-2 text-4xl font-bold text-[#FAFAFA] select-none dark:text-[#1a1a1a] sm:text-6xl">
            Detected
          </span>
          <CardHeader className="relative z-10 flex flex-row items-center justify-between px-4 sm:px-6">
            <CardTitle className="text-base font-bold text-foreground sm:text-lg">
              Total Detected
            </CardTitle>
            <span className="text-2xl font-bold text-[#DD2590] sm:text-3xl">
              {(globalStats.data?.total_ignored_entities ?? 0).toLocaleString()}
            </span>
          </CardHeader>
        </Card>
        <Card className="relative overflow-hidden">
          <span className="pointer-events-none absolute top-3 left-2 text-4xl font-bold text-[#FAFAFA] select-none dark:text-[#1a1a1a] sm:text-6xl">
            Prevented
          </span>
          <CardHeader className="relative z-10 flex flex-row items-center justify-between px-4 sm:px-6">
            <CardTitle className="text-base font-bold text-foreground sm:text-lg">
              Total Prevented
            </CardTitle>
            <span className="text-2xl font-bold text-[#DD2590] sm:text-3xl">
              {(
                globalStats.data?.total_prevented_entities ?? 0
              ).toLocaleString()}
            </span>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader className="grid grid-cols-2 gap-2">
          <CardTitle>Daily Entities</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="max-h-[30vh] w-full">
            <AreaChart data={data}>
              <CartesianGrid />
              <Area
                stackId="pii"
                type="monotone"
                dataKey="ignored_count"
                fill="var(--color-ignored_count)"
                stroke="var(--color-ignored_count)"
              />
              <Area
                stackId="pii"
                type="monotone"
                dataKey="prevented_count"
                fill="var(--color-prevented_count)"
                stroke="var(--color-prevented_count)"
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <XAxis
                dataKey="date"
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString(undefined, {
                    month: "short",
                    day: "2-digit",
                    year: undefined,
                  })
                }
              />
              <YAxis width={30} />
              <ChartLegend content={<ChartLegendContent />} />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}

function PiiChart({
  start_date,
  end_date,
  user_id,
  department_id,
}: {
  start_date: Date;
  end_date: Date;
  user_id?: string;
  department_id?: number;
}) {
  const navigate = Route.useNavigate();

  const statsByType = useQuery(
    entityCountByTypeQueryOptions({
      start_date,
      end_date,
      user_id,
      department_id,
    }),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Entities by Type</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="max-h-[35vh] w-full">
          <BarChart
            data={statsByType.data}
            onClick={(state) => {
              if (state.activeTooltipIndex == undefined) return;
              navigate({
                search: (prev) => ({
                  ...prev,
                  pii: statsByType.data?.at(
                    state.activeTooltipIndex != undefined &&
                      !Number.isNaN(Number(state.activeTooltipIndex))
                      ? Number(state.activeTooltipIndex)
                      : -1,
                  )?.entity_type_id,
                }),
                resetScroll: true,
              });
            }}
          >
            <CartesianGrid />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(label) => `Click to filter ${label}`}
                />
              }
            />
            <BarStack radius={[15, 15, 0, 0]}>
              <Bar
                stackId="pii"
                name="Detected"
                dataKey="ignored_count"
                fill="var(--color-ignored_count)"
              />
              <Bar
                stackId="pii"
                name="prevented_count"
                dataKey="prevented_count"
                fill="var(--color-prevented_count)"
              />
            </BarStack>
            <XAxis
              dataKey="entity_type"
              angle={-60}
              tick={{ textAnchor: "end", fontSize: 10 }}
              height={120}
            />
            <YAxis width={30} />
            <ChartLegend content={<ChartLegendContent />} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
